import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { 
  GraduationCap, 
  BookOpen, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Award, 
  FileText, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  ArrowLeft, 
  Copy, 
  Plus, 
  Trash2, 
  ShieldCheck, 
  Eye, 
  RefreshCw, 
  Sparkles, 
  Church, 
  Printer, 
  Check, 
  Clock, 
  HelpCircle,
  FileCheck,
  Download,
  Info,
  ChevronRight,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { phoneCode, coursesOffered } from '../../ProData';
import CustomSpinner from '../../components/customerSpinner/CustomSpinner';

const config = require('../../config.json');
const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

// Course descriptions & highlights to assist prospective students
const courseHighlights = {
  'Bachelor of Theology': {
    tagline: 'Foundational Pontifical Degree (B.Th)',
    desc: 'Comprehensive 3-year Pontifical theological formation covering Scripture, Systematic Theology, Moral & Pastoral Theology, Church History, and Canon Law.',
    eligibility: 'Requires completion of Bachelor in Philosophy (B.Ph) or equivalent university graduation.',
    icon: GraduationCap,
    badgeColor: '#1a2b56'
  },
  'Regular Theology (Diploma)': {
    tagline: 'Pastoral & Theological Diploma (RTD)',
    desc: 'Structured 2-year theological studies program focused on ministerial preparation, pastoral counseling, and ecclesial leadership.',
    eligibility: 'Exempt from mark sheet prerequisite. Open to religious and lay pastoral leaders.',
    icon: BookOpen,
    badgeColor: '#0d9488'
  },
  'One Year Theology Course': {
    tagline: 'Intensive Theological Certificate (OYT)',
    desc: 'One-year concentrated immersion in fundamental Catholic theology, spirituality, biblical hermeneutics, and Indian contextual theology.',
    eligibility: 'Ideal for religious in ongoing formation, sabbatical scholars, and committed lay leaders.',
    icon: Award,
    badgeColor: '#c69214'
  },
  'Licentiate in Sacred Theology (M.Th)': {
    tagline: 'Specialised Pontifical Research Licentiate (L.Th / M.Th)',
    desc: 'Advanced 2-year Pontifical research licentiate in Systematic Theology, Biblical Theology, or Contextual & Interreligious Theology.',
    eligibility: 'Requires Bachelor of Theology (B.Th) with minimum second class / aggregate score of 65%.',
    icon: Sparkles,
    badgeColor: '#79031d'
  },
  'Doctorate of Theology': {
    tagline: 'Pontifical Terminal Degree (D.Th)',
    desc: 'Doctoral research and dissertation contributing original theological scholarship in dialogue with Indian and global contemporary realities.',
    eligibility: 'Requires Licentiate in Sacred Theology (L.Th / S.T.L / M.Th) with distinction.',
    icon: Church,
    badgeColor: '#4f46e5'
  }
};

export default function OnlineApplication() {
  const { enqueueSnackbar } = useSnackbar();
  const [currentStep, setCurrentStep] = useState(1);
  const [loaded, setLoaded] = useState(true);

  // Profile Form State
  const [profileDetails, setProfileDetails] = useState({
    applicantName: '',
    fatherName: '',
    dateOfBirth: '',
    gender: 'Male',
    bloodGroup: '',
    nationality: 'Indian',
    religiousStatus: 'Scholastic',
    parishDiocese: '',
    congregationProvince: '',
    areaCode: '+91',
    mobileNumber: '',
    emailID: '',
    placeOfBirth: '',
    birthDistrict: '',
    birthState: '',
    localAddress: '',
    localState: '',
    localPincode: '',
    localContact: '',
    perAddress: '',
    perState: '',
    perPincode: '',
    perContactNo: '',
    courseCategory: 'Bachelor of Theology',
    degreeDetails: [],
    languageDetails: [],
    courseDetails: {}
  });

  // Uploaded Files State
  const [imageFile, setImageFile] = useState(null);
  const [certProofFile, setCertProofFile] = useState(null);
  const [markListFile, setMarkListFile] = useState(null);
  const [recommenLetterFile, setRecommenLetterFile] = useState(null);

  // Course Selection State
  const [optedCourse, setOptedCourse] = useState({
    courseName: 'Bachelor of Theology',
    courseOption: 'Correspondence'
  });
  const [selectedCourse, setSelectedCourse] = useState(coursesOffered[0] || {});

  // OTP & Submission State
  const [emailCodeReceived, setEmailCodeReceived] = useState('');
  const [emailCodeEntered, setEmailCodeEntered] = useState('');
  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [app, setApp] = useState({ appNo: '', appMsg: '' });
  const [applicationProcessed, setApplicationProcessed] = useState(false);

  // Helper State for Addresses & Degree Entry
  const [sameAsLocal, setSameAsLocal] = useState(false);
  const [newDegree, setNewDegree] = useState({ id: '', degree: '', instituteName: '', studyYear: '' });
  const [newLanguage, setNewLanguage] = useState({ id: '', language: '', reading: 'Basic', writing: 'Basic', speaking: 'Basic' });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (coursesOffered && coursesOffered.length > 0) {
      setSelectedCourse(coursesOffered[0]);
      setProfileDetails((prev) => ({
        ...prev,
        courseCategory: coursesOffered[0].courseName,
        courseDetails: coursesOffered[0]
      }));
    }
  }, []);

  // Sync Same As Local Address
  const handleSameAsLocalToggle = (e) => {
    const isChecked = e.target.checked;
    setSameAsLocal(isChecked);
    if (isChecked) {
      setProfileDetails((prev) => ({
        ...prev,
        perAddress: prev.localAddress,
        perState: prev.localState,
        perPincode: prev.localPincode,
        perContactNo: prev.localContact || prev.mobileNumber
      }));
    }
  };

  // Keep permanent address synced if checkbox is active
  const handleLocalAddressChange = (field, value) => {
    setProfileDetails((prev) => {
      const updated = { ...prev, [field]: value };
      if (sameAsLocal) {
        if (field === 'localAddress') updated.perAddress = value;
        if (field === 'localState') updated.perState = value;
        if (field === 'localPincode') updated.perPincode = value;
        if (field === 'localContact') updated.perContactNo = value;
      }
      return updated;
    });
  };

  // Add Academic Qualification
  const handleAddDegree = (e) => {
    if (e) e.preventDefault();
    if (!newDegree.degree.trim() || !newDegree.instituteName.trim()) {
      enqueueSnackbar('Please enter degree name and institution.', { variant: 'warning' });
      return;
    }
    const degreeObj = {
      id: Date.now().toString(),
      degree: newDegree.degree.toUpperCase(),
      instituteName: newDegree.instituteName.toUpperCase(),
      studyYear: newDegree.studyYear || ''
    };
    setProfileDetails((prev) => ({
      ...prev,
      degreeDetails: [...prev.degreeDetails, degreeObj]
    }));
    setNewDegree({ id: '', degree: '', instituteName: '', studyYear: '' });
    enqueueSnackbar('Degree added to qualifications.', { variant: 'info' });
  };

  const handleQuickAddDegree = (degreeTitle) => {
    setNewDegree((prev) => ({ ...prev, degree: degreeTitle }));
  };

  const handleRemoveDegree = (id) => {
    setProfileDetails((prev) => ({
      ...prev,
      degreeDetails: prev.degreeDetails.filter((d) => d.id !== id)
    }));
  };

  // Add Language Proficiency
  const handleAddLanguage = (e) => {
    if (e) e.preventDefault();
    if (!newLanguage.language.trim()) {
      enqueueSnackbar('Please enter language name.', { variant: 'warning' });
      return;
    }
    const langObj = {
      id: Date.now().toString(),
      language: newLanguage.language.toUpperCase(),
      reading: newLanguage.reading,
      writing: newLanguage.writing,
      speaking: newLanguage.speaking
    };
    setProfileDetails((prev) => ({
      ...prev,
      languageDetails: [...prev.languageDetails, langObj]
    }));
    setNewLanguage({ id: '', language: '', reading: 'Basic', writing: 'Basic', speaking: 'Basic' });
    enqueueSnackbar('Language added.', { variant: 'info' });
  };

  const handleQuickAddLanguage = (langName) => {
    setNewLanguage((prev) => ({ ...prev, language: langName }));
  };

  const handleRemoveLanguage = (id) => {
    setProfileDetails((prev) => ({
      ...prev,
      languageDetails: prev.languageDetails.filter((l) => l.id !== id)
    }));
  };

  // Generate Date & App Number for backend
  const generateDate = () => {
    const current_datetime = new Date();
    const date_year = current_datetime.getFullYear().toString();
    const date_month = (current_datetime.getMonth() + 1).toString().padStart(2, '0');
    const date_date = current_datetime.getDate().toString().padStart(2, '0');
    return `${date_year}-${date_month}-${date_date}`;
  };

  const generateApplicationNo = () => {
    const current_datetime = new Date();
    const fName = (profileDetails.applicantName || 'VJ').trim().replace(/\s/g, '');
    const tfName = fName.substring(0, 2).toUpperCase() || 'VJ';
    const lName = (profileDetails.fatherName || 'TH').trim().replace(/\s/g, '');
    const tlName = lName.substring(0, 2).toUpperCase() || 'TH';
    const date_year = current_datetime.getFullYear().toString().substring(2, 4);
    const date_month = (current_datetime.getMonth() + 1).toString().padStart(2, '0');
    const date_date = current_datetime.getDate().toString().padStart(2, '0');
    const date_hours = current_datetime.getHours().toString().padStart(2, '0');
    const date_minutes = current_datetime.getMinutes().toString().padStart(2, '0');
    return `${date_year}${date_month}${date_date}${date_hours}${date_minutes}${tfName}${tlName}`;
  };

  // Validation function
  const validateFields = () => {
    const {
      applicantName, fatherName, dateOfBirth, gender, bloodGroup, birthDistrict, birthState,
      religiousStatus, parishDiocese, congregationProvince, mobileNumber, emailID, placeOfBirth,
      localAddress, localState, localPincode, nationality,
      perAddress, perState, perPincode, courseCategory
    } = profileDetails;

    if (!courseCategory) {
      enqueueSnackbar('Please select an academic program.', { variant: 'warning' });
      setCurrentStep(1);
      return 'failed';
    }
    if (!applicantName.trim()) {
      enqueueSnackbar('Student Full Name is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!fatherName.trim()) {
      enqueueSnackbar("Father's / Guardian's Name is required.", { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!dateOfBirth) {
      enqueueSnackbar('Date of Birth is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!gender) {
      enqueueSnackbar('Gender is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!bloodGroup) {
      enqueueSnackbar('Blood Group is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!nationality) {
      enqueueSnackbar('Nationality is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!placeOfBirth || !birthDistrict || !birthState) {
      enqueueSnackbar('Place of Birth, District, and State are required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!religiousStatus) {
      enqueueSnackbar('Religious status is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!parishDiocese) {
      enqueueSnackbar('Parish & Diocese of origin is required.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (religiousStatus !== 'Laity' && !congregationProvince) {
      enqueueSnackbar('Congregation and Province is required for religious applicants.', { variant: 'warning' });
      setCurrentStep(2);
      return 'failed';
    }
    if (!emailID || !emailID.match(validRegex)) {
      enqueueSnackbar('Please enter a valid email address.', { variant: 'warning' });
      setCurrentStep(3);
      return 'failed';
    }
    if (!mobileNumber) {
      enqueueSnackbar('Mobile number is required.', { variant: 'warning' });
      setCurrentStep(3);
      return 'failed';
    }
    if (!localAddress || !localState || !localPincode) {
      enqueueSnackbar('Complete Local Address is required.', { variant: 'warning' });
      setCurrentStep(3);
      return 'failed';
    }
    if (!perAddress || !perState || !perPincode) {
      enqueueSnackbar('Complete Permanent Address is required.', { variant: 'warning' });
      setCurrentStep(3);
      return 'failed';
    }
    if (!imageFile) {
      enqueueSnackbar('Please attach your passport size photograph.', { variant: 'warning' });
      setCurrentStep(5);
      return 'failed';
    }
    if (!certProofFile) {
      enqueueSnackbar('Please attach your UG/PG qualifying degree certificate.', { variant: 'warning' });
      setCurrentStep(5);
      return 'failed';
    }
    if (!markListFile && optedCourse.courseName !== 'Regular Theology (Diploma)') {
      enqueueSnackbar('Please attach your academic mark list / transcript.', { variant: 'warning' });
      setCurrentStep(5);
      return 'failed';
    }

    return 'passed';
  };

  // Step 5 -> Step 6: Request Email OTP & Go to Review
  const handleProceedToReview = async (e) => {
    if (e) e.preventDefault();
    const result = validateFields();
    if (result !== 'passed') return;

    try {
      setIsOtpSending(true);
      setLoaded(false);
      const response = await axios.get(
        `${config.api.invokeURL}/form/registration/emailvalidation?emailID=${profileDetails.emailID}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );

      if (response.status === 200) {
        setEmailCodeReceived(response.data);
        setIsEmailValidated(true);
        setCurrentStep(6);
        window.scrollTo(0, 0);
        enqueueSnackbar(`One-Time Password (OTP) dispatched to ${profileDetails.emailID}`, { variant: 'success' });
      } else {
        enqueueSnackbar('Duplicate Records Found or Email Verification Failed.', { variant: 'warning' });
      }
    } catch (error) {
      enqueueSnackbar('Error sending verification email - ' + error.message, { variant: 'warning' });
    } finally {
      setIsOtpSending(false);
      setLoaded(true);
    }
  };

  // Resend OTP
  const handleResendOtp = async (e) => {
    if (e) e.preventDefault();
    try {
      setIsOtpSending(true);
      const response = await axios.get(
        `${config.api.invokeURL}/form/registration/emailvalidation?emailID=${profileDetails.emailID}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );
      if (response.status === 200) {
        setEmailCodeReceived(response.data);
        enqueueSnackbar('New OTP sent to ' + profileDetails.emailID, { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to resend OTP - ' + error.message, { variant: 'warning' });
    } finally {
      setIsOtpSending(false);
    }
  };

  // Upload S3 Helper (100% preserves original contract)
  const postOnS3 = async (applicationno, timageFile, inputType) => {
    try {
      if (!timageFile) return;
      let tKeyName = '';
      let newFileName = '';
      let tempMsg = '';

      if (inputType === 'Profile') {
        tKeyName = '/avatar/' + applicationno;
        newFileName = applicationno + '.jpeg';
        tempMsg = 'Profile picture uploaded successfully!';
      } else if (inputType === 'Recomm') {
        tKeyName = '/avatar/' + applicationno + '/docs';
        newFileName = applicationno + '_recommLetter.jpeg';
        tempMsg = 'Recommendation letter uploaded!';
      } else if (inputType === 'CERTProof') {
        tKeyName = '/avatar/' + applicationno + '/docs';
        newFileName = applicationno + '_certproof.jpeg';
        tempMsg = 'Degree certificate uploaded!';
      } else if (inputType === 'MarkList') {
        tKeyName = '/avatar/' + applicationno + '/docs';
        newFileName = applicationno + '_markproof.jpeg';
        tempMsg = 'Academic mark list uploaded!';
      }

      const file = timageFile;
      const blob = file.slice(0, file.size, 'image/jpeg');
      const newFile = new File([blob], newFileName, { type: 'image/jpeg' });
      const bucketName = config.s3.avatarbucketName + tKeyName;

      const response = await axios.get(
        `${config.api.invokeURL}/gallery/newgallery?fileName=${newFileName}&bucketName=${bucketName}`,
        {
          headers: {
            'x-api-key': config.api.key
          }
        }
      );

      const res = await fetch(response.data.uploadURL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'image/jpeg'
        },
        body: newFile
      });

      if (res.status === 200) {
        enqueueSnackbar(tempMsg, { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Attachment upload status: ' + error.message, { variant: 'warning' });
    }
  };

  // Final Submit Handler
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    const result = validateFields();
    if (result !== 'passed') return;

    // Verify OTP if present
    if (emailCodeReceived && emailCodeEntered.trim() !== emailCodeReceived.toString().trim()) {
      enqueueSnackbar('Invalid One Time Password (OTP). Please verify the code sent to your email.', { variant: 'error' });
      return;
    }

    try {
      setLoaded(false);
      const {
        applicantName, fatherName, dateOfBirth, gender, bloodGroup, birthDistrict, birthState,
        religiousStatus, parishDiocese, congregationProvince, areaCode, mobileNumber, emailID,
        placeOfBirth, localAddress, localState, localPincode, nationality, localContact,
        perAddress, perState, perPincode, perContactNo, courseCategory,
        degreeDetails, languageDetails, courseDetails
      } = profileDetails;

      const clientID = 'VJCollege';
      const applicationno = generateApplicationNo();
      const applicationDate = generateDate();
      const applicationStatus = 'Pending Approval';
      const avatar = applicationno + '.jpeg';
      const trecommLetter = applicationno + '_recommLetter.jpeg';
      const tcertproofFile = applicationno + '_certproof.jpeg';
      const tmarkListFile = applicationno + '_markproof.jpeg';
      const tcourseStatus = 'Pending Approval';
      const tcoursePayment = 'Pending';

      const params = {
        clientID,
        applicationNo: applicationno,
        applicationDate,
        applicationStatus,
        applicantName,
        fatherName,
        birthDistrict,
        birthState,
        dateOfBirth,
        gender,
        bloodGroup,
        religiousStatus,
        parishDiocese,
        congregationProvince,
        placeOfBirth,
        localAddress,
        areaCode,
        mobileNumber,
        emailID,
        localState,
        localPincode,
        localContact,
        perAddress,
        nationality,
        perState,
        perPincode,
        selectedCourse,
        perContactNo,
        avatar,
        recommLetter: trecommLetter,
        markListFile: tmarkListFile,
        certproofFile: tcertproofFile,
        courseStatus: tcourseStatus,
        coursePayment: tcoursePayment,
        courseCategory,
        degreeDetails,
        languageDetails,
        courseDetails
      };

      await axios.post(`${config.api.invokeURL}/dashboard/registration/${applicationno}`, params, {
        headers: {
          'x-api-key': config.api.key
        }
      });

      // Upload all files asynchronously to S3
      await postOnS3(applicationno, imageFile, 'Profile');
      if (recommenLetterFile) await postOnS3(applicationno, recommenLetterFile, 'Recomm');
      await postOnS3(applicationno, certProofFile, 'CERTProof');
      if (optedCourse.courseName !== 'Regular Theology (Diploma)' && markListFile) {
        await postOnS3(applicationno, markListFile, 'MarkList');
      }

      setApplicationProcessed(true);
      setApp({
        appNo: applicationno,
        appMsg: 'Your online application has been registered successfully. A confirmation email has been dispatched with your registration reference. The Admissions Board will review your dossier and notify you regarding interview and enrollment procedures.'
      });
      enqueueSnackbar(`Application ${applicationno} registered successfully!`, { variant: 'success' });
      setCurrentStep(7);
      window.scrollTo(0, 0);
    } catch (error) {
      enqueueSnackbar('Submission error: ' + error.message, { variant: 'error' });
    } finally {
      setLoaded(true);
    }
  };

  // Reset & New Form
  const handleNewForm = () => {
    setProfileDetails({
      applicantName: '',
      fatherName: '',
      dateOfBirth: '',
      gender: 'Male',
      bloodGroup: '',
      nationality: 'Indian',
      religiousStatus: 'Scholastic',
      parishDiocese: '',
      congregationProvince: '',
      areaCode: '+91',
      mobileNumber: '',
      emailID: '',
      placeOfBirth: '',
      birthDistrict: '',
      birthState: '',
      localAddress: '',
      localState: '',
      localPincode: '',
      localContact: '',
      perAddress: '',
      perState: '',
      perPincode: '',
      perContactNo: '',
      courseCategory: 'Bachelor of Theology',
      degreeDetails: [],
      languageDetails: [],
      courseDetails: coursesOffered[0] || {}
    });
    setImageFile(null);
    setMarkListFile(null);
    setCertProofFile(null);
    setRecommenLetterFile(null);
    setEmailCodeEntered('');
    setEmailCodeReceived('');
    setIsEmailValidated(false);
    setApplicationProcessed(false);
    setCurrentStep(1);
    window.scrollTo(0, 0);
  };

  // Steps definition for top wizard
  const steps = [
    { id: 1, title: 'Program', subtitle: 'Degree Choice', icon: GraduationCap },
    { id: 2, title: 'Profile', subtitle: 'Identity & Ecclesia', icon: User },
    { id: 3, title: 'Contact', subtitle: 'Addresses & Phone', icon: MapPin },
    { id: 4, title: 'Academics', subtitle: 'Degrees & Languages', icon: BookOpen },
    { id: 5, title: 'Documents', subtitle: 'Certificates & Photo', icon: UploadCloud },
    { id: 6, title: 'Review', subtitle: 'Verify & Confirm', icon: ShieldCheck }
  ];

  return (
    <div className="vj-online-application-page py-4 py-md-5" style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Helmet>
        <title>Online Admission Application 2025–2026 - Vidyajyoti College of Theology</title>
        <meta name="description" content="Apply online for Bachelor of Theology (B.Th), Licentiate in Sacred Theology (M.Th), and Diploma courses at Vidyajyoti College of Theology, Delhi." />
        <link rel="canonical" href="/admission/application-form" />
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            .vj-printable-receipt, .vj-printable-receipt * {
              visibility: visible;
            }
            .vj-printable-receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
              background: #ffffff !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}</style>
      </Helmet>

      {!loaded && <CustomSpinner />}

      <div className="container" style={{ maxWidth: '1080px' }}>
        {/* Portal Top Header */}
        <div className="text-center mx-auto pb-4 no-print">
          <div 
            className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-3 shadow-xs" 
            style={{ 
              background: 'linear-gradient(135deg, rgba(121, 3, 29, 0.08) 0%, rgba(26, 43, 86, 0.08) 100%)', 
              color: '#79031d', 
              fontSize: '0.85rem', 
              fontWeight: 700, 
              border: '1px solid rgba(121, 3, 29, 0.18)',
              letterSpacing: '0.04em'
            }}
          >
            <Sparkles size={15} className="text-warning" /> PONTIFICAL FACULTY OF THEOLOGY • ADMISSIONS PORTAL
          </div>
          <h1 className="display-6 fw-bold mb-2" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Online Application for Admission
          </h1>
          <p className="text-muted small mb-0" style={{ maxWidth: '680px', margin: '0 auto', fontSize: '0.92rem' }}>
            Forming men and women in theological depth, spiritual discernment, and contextual pastoral ministry in India and Asia.
          </p>
        </div>

        {/* Wizard Stepper Progress Bar (Steps 1 to 6) */}
        {currentStep <= 6 && (
          <div className="card border-0 rounded-4 shadow-sm p-3 p-md-4 bg-white mb-4 no-print">
            <div className="d-flex justify-content-between align-items-center position-relative">
              {steps.map((stepItem) => {
                const StepIcon = stepItem.icon;
                const isPassed = currentStep > stepItem.id;
                const isCurrent = currentStep === stepItem.id;

                return (
                  <div 
                    key={stepItem.id} 
                    className="d-flex flex-column align-items-center text-center cursor-pointer position-relative"
                    style={{ zIndex: 2, flex: 1 }}
                    onClick={() => {
                      if (isPassed) {
                        setCurrentStep(stepItem.id);
                        window.scrollTo(0, 0);
                      }
                    }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center shadow-sm transition-all"
                      style={{
                        width: isCurrent ? '46px' : '38px',
                        height: isCurrent ? '46px' : '38px',
                        background: isPassed 
                          ? '#10b981' 
                          : isCurrent 
                            ? 'linear-gradient(135deg, #1a2b56 0%, #79031d 100%)' 
                            : '#f1f5f9',
                        color: isPassed || isCurrent ? '#ffffff' : '#64748b',
                        fontWeight: 700,
                        fontSize: isCurrent ? '1rem' : '0.85rem',
                        border: isCurrent ? '3px solid #fef3c7' : '1px solid #e2e8f0',
                        boxShadow: isCurrent ? '0 4px 12px rgba(26, 43, 86, 0.25)' : 'none'
                      }}
                      title={`Step ${stepItem.id}: ${stepItem.title}`}
                    >
                      {isPassed ? <Check size={20} strokeWidth={2.5} /> : <StepIcon size={isCurrent ? 22 : 18} />}
                    </div>
                    <span 
                      className={`small mt-1.5 d-none d-md-block ${isCurrent ? 'fw-bold text-dark' : 'text-muted'}`}
                      style={{ fontSize: '0.8rem', lineHeight: '1.2' }}
                    >
                      {stepItem.title}
                    </span>
                    <span 
                      className="text-muted d-none d-lg-block"
                      style={{ fontSize: '0.7rem', opacity: isCurrent ? 0.9 : 0.6 }}
                    >
                      {stepItem.subtitle}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Progress Bar Line */}
            <div className="progress mt-3.5 bg-light" style={{ height: '6px', borderRadius: '10px' }}>
              <div 
                className="progress-bar rounded-pill transition-all" 
                role="progressbar" 
                style={{ 
                  width: `${((currentStep - 1) / 5) * 100}%`, 
                  background: 'linear-gradient(90deg, #1a2b56 0%, #79031d 100%)',
                  transition: 'width 0.4s ease-in-out'
                }} 
              />
            </div>
          </div>
        )}

        {/* STEP 1: Interactive Academic Course Selector */}
        {currentStep === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                <div className="p-3 rounded-4 bg-light text-primary">
                  <GraduationCap size={28} />
                </div>
                <div>
                  <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                    Select Your Academic Program
                  </h3>
                  <p className="text-muted small mb-0">Choose the theological degree, licentiate, or diploma program you wish to enroll in</p>
                </div>
              </div>

              {/* Course Selection Cards */}
              <div className="row g-3 mb-4">
                {coursesOffered.map((course, idx) => {
                  const isSelected = optedCourse.courseName === course.courseName;
                  const info = courseHighlights[course.courseName] || {
                    tagline: course.courseName,
                    desc: 'Comprehensive curriculum designed for theology students and pastoral ministers.',
                    eligibility: 'Standard qualifying academic credentials.',
                    badgeColor: '#1a2b56'
                  };
                  const CardIcon = info.icon || GraduationCap;

                  return (
                    <div key={idx} className="col-md-6 col-lg-4">
                      <div
                        onClick={() => {
                          setOptedCourse({
                            courseName: course.courseName,
                            courseOption: course.courseOption
                          });
                          setSelectedCourse(course);
                          setProfileDetails((prev) => ({
                            ...prev,
                            courseCategory: course.courseName,
                            courseDetails: course
                          }));
                        }}
                        className={`p-3.5 rounded-4 h-100 cursor-pointer transition-all d-flex flex-column justify-content-between position-relative ${
                          isSelected ? 'shadow-md border-2' : 'shadow-xs border bg-white'
                        }`}
                        style={{
                          borderColor: isSelected ? '#1a2b56' : '#e2e8f0',
                          background: isSelected ? 'linear-gradient(180deg, rgba(26, 43, 86, 0.04) 0%, #ffffff 100%)' : '#ffffff',
                          transform: isSelected ? 'translateY(-2px)' : 'none',
                          transition: 'all 0.25s ease'
                        }}
                      >
                        {/* Top Header */}
                        <div>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <span 
                              className="badge rounded-pill px-2.5 py-1 text-white fw-bold"
                              style={{ background: isSelected ? '#1a2b56' : '#64748b', fontSize: '0.75rem' }}
                            >
                              {course.courseCode}
                            </span>
                            <div 
                              className="rounded-circle d-flex align-items-center justify-content-center shadow-xs"
                              style={{
                                width: '24px',
                                height: '24px',
                                background: isSelected ? '#10b981' : '#f1f5f9',
                                color: isSelected ? '#ffffff' : '#cbd5e1',
                                border: '1px solid ' + (isSelected ? '#10b981' : '#e2e8f0')
                              }}
                            >
                              {isSelected ? <Check size={15} strokeWidth={3} /> : null}
                            </div>
                          </div>

                          <h5 className="fw-bold mb-1" style={{ color: '#1a2b56', fontSize: '1.08rem' }}>
                            {course.courseName}
                          </h5>

                          <p className="text-secondary small fw-semibold mb-2" style={{ fontSize: '0.78rem' }}>
                            {info.tagline}
                          </p>

                          <p className="text-muted small mb-3" style={{ fontSize: '0.82rem', lineHeight: '1.45' }}>
                            {info.desc}
                          </p>
                        </div>

                        {/* Eligibility & Duration Footer */}
                        <div className="pt-2 border-top">
                          <div className="d-flex align-items-center justify-content-between mb-1.5">
                            <span className="badge bg-light text-dark border small d-inline-flex align-items-center gap-1">
                              <Clock size={12} /> {course.duration}
                            </span>
                            <span className="badge bg-light text-secondary border small">
                              {course.courseOption}
                            </span>
                          </div>
                          <p className="text-muted mb-0" style={{ fontSize: '0.72rem', fontStyle: 'italic' }}>
                            {info.eligibility}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Button */}
              <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                <div className="small text-muted d-none d-sm-block">
                  Selected Program: <strong className="text-dark">{profileDetails.courseCategory}</strong>
                </div>
                <button
                  type="button"
                  className="btn rounded-pill px-4 py-2.5 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{ background: '#1a2b56' }}
                  onClick={() => {
                    setCurrentStep(2);
                    window.scrollTo(0, 0);
                  }}
                >
                  <span>Proceed to Profile Details</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 2: Personal Profile & Ecclesial Affiliation */}
        {currentStep === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                <div className="p-3 rounded-4 bg-light text-burgundy" style={{ color: '#79031d' }}>
                  <User size={28} />
                </div>
                <div>
                  <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                    Personal Details &amp; Ecclesial Profile
                  </h3>
                  <p className="text-muted small mb-0">Enter the applicant&rsquo;s official personal identity matching prior educational certificates</p>
                </div>
              </div>

              <form>
                {/* 1. Candidate Bio */}
                <h6 className="fw-bold text-uppercase text-secondary mb-3 small" style={{ letterSpacing: '0.05em' }}>
                  1. Candidate Identity
                </h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-dark">
                      Student Full Name (As in Official Certificates) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. FR. JOHN MICHAEL SJ / BRO. PETER PAUL"
                      value={profileDetails.applicantName}
                      onChange={(e) => setProfileDetails({ ...profileDetails, applicantName: e.target.value.toUpperCase() })}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-dark">
                      Father&rsquo;s / Guardian&rsquo;s Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. JOSEPH MICHAEL"
                      value={profileDetails.fatherName}
                      onChange={(e) => setProfileDetails({ ...profileDetails, fatherName: e.target.value.toUpperCase() })}
                    />
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <label className="form-label small fw-bold text-dark">
                      Date of Birth <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control rounded-3 py-2"
                      value={profileDetails.dateOfBirth}
                      onChange={(e) => setProfileDetails({ ...profileDetails, dateOfBirth: e.target.value })}
                    />
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <label className="form-label small fw-bold text-dark">
                      Gender <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select rounded-3 py-2"
                      value={profileDetails.gender}
                      onChange={(e) => setProfileDetails({ ...profileDetails, gender: e.target.value })}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <label className="form-label small fw-bold text-dark">
                      Blood Group <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select rounded-3 py-2"
                      value={profileDetails.bloodGroup}
                      onChange={(e) => setProfileDetails({ ...profileDetails, bloodGroup: e.target.value })}
                    >
                      <option value="">Select Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>

                  <div className="col-sm-6 col-md-3">
                    <label className="form-label small fw-bold text-dark">
                      Nationality <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. INDIAN"
                      value={profileDetails.nationality}
                      onChange={(e) => setProfileDetails({ ...profileDetails, nationality: e.target.value.toUpperCase() })}
                    />
                  </div>
                </div>

                {/* 2. Place of Birth */}
                <h6 className="fw-bold text-uppercase text-secondary mb-3 small" style={{ letterSpacing: '0.05em' }}>
                  2. Place of Birth
                </h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">
                      Place / Village / Town <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. Kanjirapally / Ranchi"
                      value={profileDetails.placeOfBirth}
                      onChange={(e) => setProfileDetails({ ...profileDetails, placeOfBirth: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">
                      District <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. Kottayam"
                      value={profileDetails.birthDistrict}
                      onChange={(e) => setProfileDetails({ ...profileDetails, birthDistrict: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">
                      State <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. Kerala / Jharkhand"
                      value={profileDetails.birthState}
                      onChange={(e) => setProfileDetails({ ...profileDetails, birthState: e.target.value.toUpperCase() })}
                    />
                  </div>
                </div>

                {/* 3. Ecclesial & Religious Background */}
                <h6 className="fw-bold text-uppercase text-secondary mb-3 small" style={{ letterSpacing: '0.05em' }}>
                  3. Ecclesial &amp; Religious Affiliation
                </h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">
                      Religious Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select rounded-3 py-2"
                      value={profileDetails.religiousStatus}
                      onChange={(e) => setProfileDetails({ ...profileDetails, religiousStatus: e.target.value })}
                    >
                      <option value="Scholastic">Scholastic (Jesuit / Religious)</option>
                      <option value="Brothers">Religious Brother</option>
                      <option value="Sisters">Woman Religious (Sister)</option>
                      <option value="Father">Priest / Father</option>
                      <option value="Laity">Lay Person (Laity)</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">
                      Parish &amp; Diocese of Origin <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="e.g. St. Joseph, Delhi Archdiocese"
                      value={profileDetails.parishDiocese}
                      onChange={(e) => setProfileDetails({ ...profileDetails, parishDiocese: e.target.value.toUpperCase() })}
                    />
                  </div>

                  {profileDetails.religiousStatus !== 'Laity' && (
                    <div className="col-md-4">
                      <label className="form-label small fw-bold text-dark">
                        Congregation &amp; Province <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-3 py-2"
                        placeholder="e.g. Society of Jesus, Delhi Province"
                        value={profileDetails.congregationProvince}
                        onChange={(e) => setProfileDetails({ ...profileDetails, congregationProvince: e.target.value.toUpperCase() })}
                      />
                    </div>
                  )}
                </div>

                {/* Stepper Navigation */}
                <div className="d-flex justify-content-between pt-3 border-top">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-1.5"
                    onClick={() => {
                      setCurrentStep(1);
                      window.scrollTo(0, 0);
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Program
                  </button>
                  <button
                    type="button"
                    className="btn rounded-pill px-4 py-2 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                    style={{ background: '#1a2b56' }}
                    onClick={() => {
                      if (!profileDetails.applicantName || !profileDetails.fatherName || !profileDetails.dateOfBirth) {
                        enqueueSnackbar('Please fill in candidate name, father name, and birth date.', { variant: 'warning' });
                        return;
                      }
                      setCurrentStep(3);
                      window.scrollTo(0, 0);
                    }}
                  >
                    <span>Next: Contact &amp; Addresses</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}

        {/* STEP 3: Contact & Address Information */}
        {currentStep === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                <div className="p-3 rounded-4 bg-light text-primary">
                  <MapPin size={28} />
                </div>
                <div>
                  <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                    Contact &amp; Address Details
                  </h3>
                  <p className="text-muted small mb-0">Official communication coordinates and residential records</p>
                </div>
              </div>

              {/* 1. Direct Communication Channels */}
              <h6 className="fw-bold text-uppercase text-secondary mb-3 small" style={{ letterSpacing: '0.05em' }}>
                1. Communication Coordinates
              </h6>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">
                    Email Address (OTP will be sent here) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted border-end-0">
                      <Mail size={16} />
                    </span>
                    <input
                      type="email"
                      className="form-control rounded-end-3 py-2 border-start-0"
                      placeholder="student@example.com"
                      value={profileDetails.emailID}
                      onChange={(e) => setProfileDetails({ ...profileDetails, emailID: e.target.value.trim() })}
                    />
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Double check spelling. Your verification OTP will be sent to this inbox.
                  </small>
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">
                    Primary Mobile Number <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <select
                      className="form-select bg-light"
                      style={{ maxWidth: '100px' }}
                      value={profileDetails.areaCode}
                      onChange={(e) => setProfileDetails({ ...profileDetails, areaCode: e.target.value })}
                    >
                      {phoneCode.map((item, idx) => (
                        <option key={idx} value={item.Code}>{item.Code}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      className="form-control rounded-end-3 py-2"
                      placeholder="10-digit mobile number"
                      value={profileDetails.mobileNumber}
                      onChange={(e) => setProfileDetails({ ...profileDetails, mobileNumber: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* 2. Local / Current Address */}
              <div className="p-3.5 rounded-4 bg-light border mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <MapPin size={18} className="text-primary" />
                  <h6 className="fw-bold text-dark mb-0">Local / Present Mailing Address</h6>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label small fw-bold text-dark">
                      Street Address / House No. / Formation House <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="e.g. Vidyajyoti Residence, 23 Raj Niwas Marg"
                      value={profileDetails.localAddress}
                      onChange={(e) => handleLocalAddressChange('localAddress', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">State <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="e.g. Delhi"
                      value={profileDetails.localState}
                      onChange={(e) => handleLocalAddressChange('localState', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">Postal PIN Code <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="e.g. 110054"
                      value={profileDetails.localPincode}
                      onChange={(e) => handleLocalAddressChange('localPincode', e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">Local Phone / Alternate Contact</label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="Alternate phone number"
                      value={profileDetails.localContact}
                      onChange={(e) => handleLocalAddressChange('localContact', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* 3. Permanent Address */}
              <div className="p-3.5 rounded-4 bg-light border mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <MapPin size={18} className="text-burgundy" style={{ color: '#79031d' }} />
                    <h6 className="fw-bold text-dark mb-0">Permanent / Native Address</h6>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sameAsLocalCheck"
                      checked={sameAsLocal}
                      onChange={handleSameAsLocalToggle}
                    />
                    <label className="form-check-label small fw-bold text-primary cursor-pointer" htmlFor="sameAsLocalCheck">
                      <Copy size={13} className="me-1 inline-block" /> Same as Local Address
                    </label>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label small fw-bold text-dark">
                      Permanent Street Address / Village <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="Permanent address"
                      value={profileDetails.perAddress}
                      disabled={sameAsLocal}
                      onChange={(e) => setProfileDetails({ ...profileDetails, perAddress: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">State <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="State"
                      value={profileDetails.perState}
                      disabled={sameAsLocal}
                      onChange={(e) => setProfileDetails({ ...profileDetails, perState: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">Postal PIN Code <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="PIN code"
                      value={profileDetails.perPincode}
                      disabled={sameAsLocal}
                      onChange={(e) => setProfileDetails({ ...profileDetails, perPincode: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold text-dark">Guardian / Parish Contact</label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2 bg-white"
                      placeholder="Guardian phone number"
                      value={profileDetails.perContactNo}
                      disabled={sameAsLocal}
                      onChange={(e) => setProfileDetails({ ...profileDetails, perContactNo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="d-flex justify-content-between pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-1.5"
                  onClick={() => {
                    setCurrentStep(2);
                    window.scrollTo(0, 0);
                  }}
                >
                  <ArrowLeft size={16} /> Back to Profile
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-4 py-2 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{ background: '#1a2b56' }}
                  onClick={() => {
                    if (!profileDetails.emailID || !profileDetails.mobileNumber || !profileDetails.localAddress) {
                      enqueueSnackbar('Please fill in email, mobile number, and local address.', { variant: 'warning' });
                      return;
                    }
                    setCurrentStep(4);
                    window.scrollTo(0, 0);
                  }}
                >
                  <span>Next: Academics &amp; Languages</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 4: Academic Background & Language Proficiency */}
        {currentStep === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                <div className="p-3 rounded-4 bg-light text-primary">
                  <BookOpen size={28} />
                </div>
                <div>
                  <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                    Academic Qualifications &amp; Languages
                  </h3>
                  <p className="text-muted small mb-0">Record your previous degrees, philosophical studies, diplomas, and linguistic competencies</p>
                </div>
              </div>

              {/* 1. Academic Degrees Matrix */}
              <div className="mb-5">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <GraduationCap size={18} className="text-primary" /> Prior Academic Degrees &amp; Diplomas
                  </h6>
                  <span className="badge bg-light text-dark border px-2.5 py-1">
                    {profileDetails.degreeDetails.length} {profileDetails.degreeDetails.length === 1 ? 'Degree' : 'Degrees'} Added
                  </span>
                </div>

                {/* Quick Add Chips */}
                <div className="d-flex flex-wrap align-items-center gap-1.5 mb-3">
                  <span className="text-muted small me-1">Quick Select:</span>
                  {['B.Ph. (Philosophy)', 'B.A. Philosophy', 'B.A. English', 'B.Sc.', 'B.Com.', 'M.A.', 'B.Th.'].map((title, i) => (
                    <button
                      key={i}
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill px-2.5 py-0.5 small"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => handleQuickAddDegree(title)}
                    >
                      + {title}
                    </button>
                  ))}
                </div>

                {/* Inline Degree Adder Form */}
                <div className="p-3.5 rounded-4 bg-light border mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label small fw-bold text-dark mb-1">Degree / Course Name</label>
                      <input
                        type="text"
                        className="form-control rounded-3 py-2 bg-white"
                        placeholder="e.g. B.Ph. / B.A."
                        value={newDegree.degree}
                        onChange={(e) => setNewDegree({ ...newDegree, degree: e.target.value })}
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label small fw-bold text-dark mb-1">Institute &amp; University</label>
                      <input
                        type="text"
                        className="form-control rounded-3 py-2 bg-white"
                        placeholder="e.g. Jnana Deepa Vidyapeeth / Satya Nilayam"
                        value={newDegree.instituteName}
                        onChange={(e) => setNewDegree({ ...newDegree, instituteName: e.target.value })}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small fw-bold text-dark mb-1">Completion Year</label>
                      <input
                        type="text"
                        className="form-control rounded-3 py-2 bg-white"
                        placeholder="e.g. 2023"
                        value={newDegree.studyYear}
                        onChange={(e) => setNewDegree({ ...newDegree, studyYear: e.target.value })}
                      />
                    </div>
                    <div className="col-md-1 text-end">
                      <button
                        type="button"
                        className="btn btn-dark rounded-3 w-100 py-2 d-flex align-items-center justify-content-center"
                        style={{ background: '#1a2b56' }}
                        onClick={handleAddDegree}
                        title="Add Qualification"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Degrees Table */}
                {profileDetails.degreeDetails.length > 0 ? (
                  <div className="table-responsive rounded-4 border">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr className="small text-muted text-uppercase" style={{ fontSize: '0.78rem' }}>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Degree / Examination</th>
                          <th>Institution / University</th>
                          <th>Year</th>
                          <th className="text-end">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileDetails.degreeDetails.map((deg, idx) => (
                          <tr key={deg.id || idx}>
                            <td className="fw-bold small text-muted">{idx + 1}</td>
                            <td className="fw-bold text-dark">{deg.degree}</td>
                            <td className="text-secondary small">{deg.instituteName}</td>
                            <td>
                              <span className="badge bg-light text-dark border px-2.5 py-1">{deg.studyYear || '—'}</span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger border-0 rounded-circle p-1.5"
                                onClick={() => handleRemoveDegree(deg.id)}
                                title="Remove qualification"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-3 text-center text-muted border rounded-4 bg-white small">
                    No academic qualifications recorded yet. Use the fields above to add your qualifying degrees.
                  </div>
                )}
              </div>

              {/* 2. Language Proficiency Matrix */}
              <div className="mb-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <Award size={18} className="text-burgundy" style={{ color: '#79031d' }} /> Language Proficiencies
                  </h6>
                  <span className="badge bg-light text-dark border px-2.5 py-1">
                    {profileDetails.languageDetails.length} {profileDetails.languageDetails.length === 1 ? 'Language' : 'Languages'} Added
                  </span>
                </div>

                {/* Quick Add Language Chips */}
                <div className="d-flex flex-wrap align-items-center gap-1.5 mb-3">
                  <span className="text-muted small me-1">Quick Select:</span>
                  {['English', 'Hindi', 'Latin', 'Biblical Greek', 'Biblical Hebrew', 'Tamil', 'Malayalam', 'Santhali', 'Bengali'].map((lang, i) => (
                    <button
                      key={i}
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill px-2.5 py-0.5 small"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => handleQuickAddLanguage(lang)}
                    >
                      + {lang}
                    </button>
                  ))}
                </div>

                {/* Inline Language Form */}
                <div className="p-3.5 rounded-4 bg-light border mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label small fw-bold text-dark mb-1">Language</label>
                      <input
                        type="text"
                        className="form-control rounded-3 py-2 bg-white"
                        placeholder="e.g. English, Latin"
                        value={newLanguage.language}
                        onChange={(e) => setNewLanguage({ ...newLanguage, language: e.target.value })}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-bold text-dark mb-1">Reading</label>
                      <select
                        className="form-select rounded-3 py-2 bg-white"
                        value={newLanguage.reading}
                        onChange={(e) => setNewLanguage({ ...newLanguage, reading: e.target.value })}
                      >
                        <option value="Basic">Basic</option>
                        <option value="Fluent">Fluent</option>
                        <option value="Mother Tongue">Mother Tongue</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-bold text-dark mb-1">Writing</label>
                      <select
                        className="form-select rounded-3 py-2 bg-white"
                        value={newLanguage.writing}
                        onChange={(e) => setNewLanguage({ ...newLanguage, writing: e.target.value })}
                      >
                        <option value="Basic">Basic</option>
                        <option value="Fluent">Fluent</option>
                        <option value="Mother Tongue">Mother Tongue</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small fw-bold text-dark mb-1">Speaking</label>
                      <select
                        className="form-select rounded-3 py-2 bg-white"
                        value={newLanguage.speaking}
                        onChange={(e) => setNewLanguage({ ...newLanguage, speaking: e.target.value })}
                      >
                        <option value="Basic">Basic</option>
                        <option value="Fluent">Fluent</option>
                        <option value="Mother Tongue">Mother Tongue</option>
                      </select>
                    </div>
                    <div className="col-md-1 text-end">
                      <button
                        type="button"
                        className="btn btn-dark rounded-3 w-100 py-2 d-flex align-items-center justify-content-center"
                        style={{ background: '#1a2b56' }}
                        onClick={handleAddLanguage}
                        title="Add Language"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Languages Table */}
                {profileDetails.languageDetails.length > 0 ? (
                  <div className="table-responsive rounded-4 border">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr className="small text-muted text-uppercase" style={{ fontSize: '0.78rem' }}>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Language</th>
                          <th>Reading</th>
                          <th>Writing</th>
                          <th>Speaking</th>
                          <th className="text-end">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profileDetails.languageDetails.map((lang, idx) => (
                          <tr key={lang.id || idx}>
                            <td className="fw-bold small text-muted">{idx + 1}</td>
                            <td className="fw-bold text-dark">{lang.language}</td>
                            <td><span className="badge bg-light text-secondary border px-2.5 py-1">{lang.reading}</span></td>
                            <td><span className="badge bg-light text-secondary border px-2.5 py-1">{lang.writing}</span></td>
                            <td><span className="badge bg-light text-secondary border px-2.5 py-1">{lang.speaking}</span></td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger border-0 rounded-circle p-1.5"
                                onClick={() => handleRemoveLanguage(lang.id)}
                                title="Remove language"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-3 text-center text-muted border rounded-4 bg-white small">
                    No languages added yet. Specify your reading, writing, and speaking proficiencies above.
                  </div>
                )}
              </div>

              {/* Stepper Navigation */}
              <div className="d-flex justify-content-between pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-1.5"
                  onClick={() => {
                    setCurrentStep(3);
                    window.scrollTo(0, 0);
                  }}
                >
                  <ArrowLeft size={16} /> Back to Contact
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-4 py-2 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{ background: '#1a2b56' }}
                  onClick={() => {
                    setCurrentStep(5);
                    window.scrollTo(0, 0);
                  }}
                >
                  <span>Next: Document Uploads</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 5: Document Uploads & Previews */}
        {currentStep === 5 && (
          <motion.div
            key="step5"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                <div className="p-3 rounded-4 bg-light text-success">
                  <UploadCloud size={28} />
                </div>
                <div>
                  <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                    Upload Required Documents &amp; Certificates
                  </h3>
                  <p className="text-muted small mb-0">Please attach clear scanned images or photographs of your credentials (JPEG / PNG format)</p>
                </div>
              </div>

              <div className="row g-4 mb-4">
                {/* 1. Passport Photo */}
                <div className="col-md-6">
                  <div className="p-4 rounded-4 border bg-light h-100 d-flex flex-column justify-content-between shadow-xs">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-dark mb-0">
                          1. Passport Size Photograph <span className="text-danger">*</span>
                        </h6>
                        {imageFile ? (
                          <span className="badge bg-success text-white rounded-pill px-2.5 py-1 small fw-semibold">
                            <Check size={13} className="me-1" /> Attached
                          </span>
                        ) : (
                          <span className="badge bg-warning text-dark rounded-pill px-2.5 py-1 small fw-semibold">Required</span>
                        )}
                      </div>
                      <p className="text-muted small mb-3">Recent clear color passport photograph with formal background.</p>

                      {imageFile && (
                        <div className="mb-3 text-center p-2 rounded-3 bg-white border">
                          <img
                            src={URL.createObjectURL(imageFile)}
                            alt="Passport Preview"
                            className="rounded-circle shadow-sm mb-1"
                            style={{ width: '90px', height: '90px', objectFit: 'cover' }}
                          />
                          <small className="d-block text-truncate text-muted">{imageFile.name}</small>
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id="photoUpload"
                        className="d-none"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setImageFile(e.target.files[0]);
                            enqueueSnackbar('Photo attached: ' + e.target.files[0].name, { variant: 'success' });
                          }
                        }}
                      />
                      <label
                        htmlFor="photoUpload"
                        className="btn btn-outline-primary w-100 rounded-pill py-2.5 d-flex align-items-center justify-content-center gap-2 cursor-pointer fw-semibold"
                      >
                        <UploadCloud size={17} /> {imageFile ? 'Change Photo' : 'Select Photo (JPG/PNG)'}
                      </label>
                    </div>
                  </div>
                </div>

                {/* 2. UG/PG Degree Certificate */}
                <div className="col-md-6">
                  <div className="p-4 rounded-4 border bg-light h-100 d-flex flex-column justify-content-between shadow-xs">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-dark mb-0">
                          2. Degree / B.Ph. Certificate <span className="text-danger">*</span>
                        </h6>
                        {certProofFile ? (
                          <span className="badge bg-success text-white rounded-pill px-2.5 py-1 small fw-semibold">
                            <Check size={13} className="me-1" /> Attached
                          </span>
                        ) : (
                          <span className="badge bg-warning text-dark rounded-pill px-2.5 py-1 small fw-semibold">Required</span>
                        )}
                      </div>
                      <p className="text-muted small mb-3">Scanned copy of Bachelor in Philosophy (B.Ph), B.A., or qualifying degree certificate.</p>

                      {certProofFile && (
                        <div className="mb-3 text-center p-2 rounded-3 bg-white border">
                          <img
                            src={URL.createObjectURL(certProofFile)}
                            alt="Certificate Preview"
                            className="rounded-3 shadow-sm border mb-1"
                            style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <small className="d-block text-truncate text-muted">{certProofFile.name}</small>
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id="certUpload"
                        className="d-none"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setCertProofFile(e.target.files[0]);
                            enqueueSnackbar('Certificate attached: ' + e.target.files[0].name, { variant: 'success' });
                          }
                        }}
                      />
                      <label
                        htmlFor="certUpload"
                        className="btn btn-outline-primary w-100 rounded-pill py-2.5 d-flex align-items-center justify-content-center gap-2 cursor-pointer fw-semibold"
                      >
                        <UploadCloud size={17} /> {certProofFile ? 'Change Certificate' : 'Select Certificate (JPG/PNG)'}
                      </label>
                    </div>
                  </div>
                </div>

                {/* 3. Academic Mark List (if applicable) */}
                {optedCourse.courseName !== 'Regular Theology (Diploma)' && (
                  <div className="col-md-6">
                    <div className="p-4 rounded-4 border bg-light h-100 d-flex flex-column justify-content-between shadow-xs">
                      <div>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="fw-bold text-dark mb-0">
                            3. Philosophy / Academic Mark List <span className="text-danger">*</span>
                          </h6>
                          {markListFile ? (
                            <span className="badge bg-success text-white rounded-pill px-2.5 py-1 small fw-semibold">
                              <Check size={13} className="me-1" /> Attached
                            </span>
                          ) : (
                            <span className="badge bg-warning text-dark rounded-pill px-2.5 py-1 small fw-semibold">Required</span>
                          )}
                        </div>
                        <p className="text-muted small mb-3">Official mark sheet or academic transcript of philosophical or undergraduate studies.</p>

                        {markListFile && (
                          <div className="mb-3 text-center p-2 rounded-3 bg-white border">
                            <img
                              src={URL.createObjectURL(markListFile)}
                              alt="Marklist Preview"
                              className="rounded-3 shadow-sm border mb-1"
                              style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            <small className="d-block text-truncate text-muted">{markListFile.name}</small>
                          </div>
                        )}
                      </div>

                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          id="marklistUpload"
                          className="d-none"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setMarkListFile(e.target.files[0]);
                              enqueueSnackbar('Mark list attached: ' + e.target.files[0].name, { variant: 'success' });
                            }
                          }}
                        />
                        <label
                          htmlFor="marklistUpload"
                          className="btn btn-outline-primary w-100 rounded-pill py-2.5 d-flex align-items-center justify-content-center gap-2 cursor-pointer fw-semibold"
                        >
                          <UploadCloud size={17} /> {markListFile ? 'Change Mark List' : 'Select Mark List (JPG/PNG)'}
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* 4. Recommendation / Superior Letter */}
                <div className="col-md-6">
                  <div className="p-4 rounded-4 border bg-light h-100 d-flex flex-column justify-content-between shadow-xs">
                    <div>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-dark mb-0">
                          4. Recommendation / Superior Letter
                        </h6>
                        {recommenLetterFile ? (
                          <span className="badge bg-success text-white rounded-pill px-2.5 py-1 small fw-semibold">
                            <Check size={13} className="me-1" /> Attached
                          </span>
                        ) : (
                          <span className="badge bg-light text-secondary border rounded-pill px-2.5 py-1 small">
                            {profileDetails.religiousStatus === 'Laity' ? 'Optional' : 'Recommended'}
                          </span>
                        )}
                      </div>
                      <p className="text-muted small mb-3">Recommendation letter from Provincial / Bishop / Superior / Parish Priest.</p>

                      {recommenLetterFile && (
                        <div className="mb-3 text-center p-2 rounded-3 bg-white border">
                          <img
                            src={URL.createObjectURL(recommenLetterFile)}
                            alt="Recommendation Preview"
                            className="rounded-3 shadow-sm border mb-1"
                            style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <small className="d-block text-truncate text-muted">{recommenLetterFile.name}</small>
                        </div>
                      )}
                    </div>

                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        id="recommUpload"
                        className="d-none"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setRecommenLetterFile(e.target.files[0]);
                            enqueueSnackbar('Recommendation letter attached: ' + e.target.files[0].name, { variant: 'success' });
                          }
                        }}
                      />
                      <label
                        htmlFor="recommUpload"
                        className="btn btn-outline-primary w-100 rounded-pill py-2.5 d-flex align-items-center justify-content-center gap-2 cursor-pointer fw-semibold"
                      >
                        <UploadCloud size={17} /> {recommenLetterFile ? 'Change Letter' : 'Select Letter (JPG/PNG)'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="d-flex justify-content-between pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-1.5"
                  onClick={() => {
                    setCurrentStep(4);
                    window.scrollTo(0, 0);
                  }}
                >
                  <ArrowLeft size={16} /> Back to Academics
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-4 py-2.5 text-white fw-bold d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{ background: '#1a2b56' }}
                  disabled={isOtpSending}
                  onClick={handleProceedToReview}
                >
                  {isOtpSending ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    <>
                      <span>Review Dossier &amp; Verify Email</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 6: Review Dossier & Email OTP Verification */}
        {currentStep === 6 && (
          <motion.div
            key="step6"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
          >
            <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-3 rounded-4 bg-light text-primary">
                    <ShieldCheck size={28} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                      Review Dossier &amp; Confirm Submission
                    </h3>
                    <p className="text-muted small mb-0">Verify all candidate records and enter the one-time code sent to your email</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1.5"
                  onClick={() => {
                    setCurrentStep(2);
                    window.scrollTo(0, 0);
                  }}
                >
                  <User size={14} /> Edit Details
                </button>
              </div>

              {/* Dossier Summary Cards */}
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="p-3.5 rounded-4 bg-light border h-100">
                    <h6 className="fw-bold text-dark mb-2.5 pb-1 border-bottom d-flex align-items-center gap-2">
                      <GraduationCap size={16} className="text-primary" /> Program &amp; Student Identity
                    </h6>
                    <p className="small mb-1.5"><strong>Course Applied:</strong> <span className="text-primary fw-bold">{profileDetails.courseCategory}</span></p>
                    <p className="small mb-1.5"><strong>Candidate Full Name:</strong> {profileDetails.applicantName}</p>
                    <p className="small mb-1.5"><strong>Father&rsquo;s / Guardian&rsquo;s Name:</strong> {profileDetails.fatherName}</p>
                    <p className="small mb-1.5"><strong>Date of Birth &amp; Gender:</strong> {profileDetails.dateOfBirth} ({profileDetails.gender})</p>
                    <p className="small mb-0"><strong>Blood Group:</strong> {profileDetails.bloodGroup} | <strong>Nationality:</strong> {profileDetails.nationality}</p>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3.5 rounded-4 bg-light border h-100">
                    <h6 className="fw-bold text-dark mb-2.5 pb-1 border-bottom d-flex align-items-center gap-2">
                      <Church size={16} className="text-burgundy" style={{ color: '#79031d' }} /> Contact &amp; Ecclesia
                    </h6>
                    <p className="small mb-1.5"><strong>Email:</strong> <span className="text-dark fw-semibold">{profileDetails.emailID}</span></p>
                    <p className="small mb-1.5"><strong>Mobile:</strong> {profileDetails.areaCode} {profileDetails.mobileNumber}</p>
                    <p className="small mb-1.5"><strong>Religious Status:</strong> {profileDetails.religiousStatus}</p>
                    <p className="small mb-1.5"><strong>Parish &amp; Diocese:</strong> {profileDetails.parishDiocese}</p>
                    {profileDetails.congregationProvince && (
                      <p className="small mb-0"><strong>Congregation / Province:</strong> {profileDetails.congregationProvince}</p>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="p-3.5 rounded-4 bg-light border">
                    <h6 className="fw-bold text-dark mb-2 pb-1 border-bottom d-flex align-items-center gap-2">
                      <MapPin size={16} className="text-primary" /> Address Records
                    </h6>
                    <div className="row g-2 small">
                      <div className="col-md-6">
                        <strong>Local Present Address:</strong> {profileDetails.localAddress}, {profileDetails.localState} - {profileDetails.localPincode}
                      </div>
                      <div className="col-md-6">
                        <strong>Permanent Address:</strong> {profileDetails.perAddress}, {profileDetails.perState} - {profileDetails.perPincode}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Uploaded Documents Thumbnails */}
                <div className="col-12">
                  <div className="p-3.5 rounded-4 bg-light border">
                    <h6 className="fw-bold text-dark mb-3 pb-1 border-bottom d-flex align-items-center gap-2">
                      <FileCheck size={16} className="text-success" /> Uploaded Document Proofs
                    </h6>
                    <div className="row g-3 text-center">
                      {imageFile && (
                        <div className="col-6 col-md-3">
                          <img
                            src={URL.createObjectURL(imageFile)}
                            alt="Passport"
                            className="rounded-3 shadow-sm border mb-1.5 bg-white"
                            style={{ height: '90px', width: '90px', objectFit: 'cover' }}
                          />
                          <span className="d-block small text-muted fw-semibold">Passport Photo</span>
                        </div>
                      )}
                      {certProofFile && (
                        <div className="col-6 col-md-3">
                          <img
                            src={URL.createObjectURL(certProofFile)}
                            alt="Certificate"
                            className="rounded-3 shadow-sm border mb-1.5 bg-white p-1"
                            style={{ height: '90px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <span className="d-block small text-muted fw-semibold">Degree Certificate</span>
                        </div>
                      )}
                      {markListFile && (
                        <div className="col-6 col-md-3">
                          <img
                            src={URL.createObjectURL(markListFile)}
                            alt="Marklist"
                            className="rounded-3 shadow-sm border mb-1.5 bg-white p-1"
                            style={{ height: '90px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <span className="d-block small text-muted fw-semibold">Academic Mark List</span>
                        </div>
                      )}
                      {recommenLetterFile && (
                        <div className="col-6 col-md-3">
                          <img
                            src={URL.createObjectURL(recommenLetterFile)}
                            alt="Recommendation"
                            className="rounded-3 shadow-sm border mb-1.5 bg-white p-1"
                            style={{ height: '90px', maxWidth: '100%', objectFit: 'contain' }}
                          />
                          <span className="d-block small text-muted fw-semibold">Recommendation</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Secure OTP Verification Box */}
              <div 
                className="p-4 rounded-4 text-center my-4 border"
                style={{ background: 'rgba(26, 43, 86, 0.03)', borderColor: 'rgba(26, 43, 86, 0.2)' }}
              >
                <div 
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-2 text-white shadow-xs"
                  style={{ width: '48px', height: '48px', background: '#1a2b56' }}
                >
                  <Mail size={22} />
                </div>
                <h5 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>
                  Email Verification Code (OTP)
                </h5>
                <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: '500px', fontSize: '0.88rem' }}>
                  A verification code has been dispatched to <strong>{profileDetails.emailID}</strong>. Please enter the OTP below to validate and submit your application.
                </p>

                <div className="mx-auto mb-3" style={{ maxWidth: '280px' }}>
                  <input
                    type="text"
                    className="form-control form-control-lg text-center fw-bold rounded-pill shadow-sm"
                    placeholder="Enter OTP"
                    style={{ letterSpacing: '0.25em', fontSize: '1.25rem', borderColor: '#1a2b56' }}
                    value={emailCodeEntered}
                    onChange={(e) => setEmailCodeEntered(e.target.value)}
                  />
                </div>

                <div className="d-flex align-items-center justify-content-center gap-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none fw-semibold text-primary p-0"
                    onClick={handleResendOtp}
                    disabled={isOtpSending}
                  >
                    <RefreshCw size={14} className={`me-1 ${isOtpSending ? 'animate-spin' : ''}`} /> Resend Verification Code
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-between pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-1.5"
                  onClick={() => {
                    setCurrentStep(5);
                    window.scrollTo(0, 0);
                  }}
                >
                  <ArrowLeft size={16} /> Back to Documents
                </button>
                <button
                  type="button"
                  className="btn rounded-pill px-5 py-2.5 text-white fw-bold d-inline-flex align-items-center gap-2 shadow"
                  style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #79031d 100%)' }}
                  onClick={handleSubmit}
                >
                  <CheckCircle2 size={20} />
                  <span>Submit Application Dossier</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP 7: Application Confirmation / Success Screen */}
        {currentStep === 7 && applicationProcessed && (
          <motion.div
            key="step7"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="card border-0 rounded-4 shadow p-4 p-md-5 bg-white text-center mb-4 vj-printable-receipt">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3 text-white shadow-sm"
                style={{ width: '74px', height: '74px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                <CheckCircle2 size={40} />
              </div>

              <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-3 py-1.5 fw-semibold mb-2">
                APPLICATION REGISTERED SUCCESSFULLY
              </span>

              <h2 className="fw-bold mb-2" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
                Thank You, {profileDetails.applicantName}!
              </h2>

              <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '600px', lineHeight: '1.7', fontSize: '0.94rem' }}>
                {app.appMsg}
              </p>

              {/* Registration Reference Dossier Slip */}
              <div 
                className="p-4 rounded-4 border mx-auto mb-4 text-start"
                style={{ maxWidth: '640px', background: 'rgba(26, 43, 86, 0.02)' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <div>
                    <span className="d-block text-muted small">Application Registration Number</span>
                    <h4 className="fw-bold mb-0 text-primary">{app.appNo}</h4>
                  </div>
                  <span className="badge bg-warning text-dark px-3 py-1.5 rounded-pill fw-semibold">
                    Under Review
                  </span>
                </div>

                <div className="row g-2.5 small text-dark">
                  <div className="col-sm-6">
                    <strong>Program Applied:</strong> {profileDetails.courseCategory}
                  </div>
                  <div className="col-sm-6">
                    <strong>Date Registered:</strong> {generateDate()}
                  </div>
                  <div className="col-sm-6">
                    <strong>Registered Email:</strong> {profileDetails.emailID}
                  </div>
                  <div className="col-sm-6">
                    <strong>Contact Phone:</strong> {profileDetails.areaCode} {profileDetails.mobileNumber}
                  </div>
                  <div className="col-sm-6">
                    <strong>Religious Status:</strong> {profileDetails.religiousStatus}
                  </div>
                  <div className="col-sm-6">
                    <strong>Parish / Diocese:</strong> {profileDetails.parishDiocese}
                  </div>
                </div>

                {/* Important Next Steps Box */}
                <div className="mt-3 pt-3 border-top">
                  <h6 className="fw-bold text-dark small mb-1.5 d-flex align-items-center gap-1.5">
                    <Info size={15} className="text-primary" /> What Happens Next?
                  </h6>
                  <ol className="text-muted small ps-3 mb-0" style={{ fontSize: '0.8rem', lineHeight: '1.5' }}>
                    <li>The Admissions Committee reviews qualifying academic transcripts and letters.</li>
                    <li>Shortlisted candidates will receive an email invitation for the online / in-person interview.</li>
                    <li>Upon formal admission confirmation, enrollment fees and registration documents are finalized.</li>
                  </ol>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex flex-wrap justify-content-center gap-3 pt-2 no-print">
                <button
                  type="button"
                  className="btn btn-outline-dark rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2"
                  onClick={() => window.print()}
                >
                  <Printer size={18} /> Print / Save Confirmation
                </button>
                <button
                  type="button"
                  className="btn text-white rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                  style={{ background: '#1a2b56' }}
                  onClick={handleNewForm}
                >
                  <span>Submit Another Application</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
